// API Base URL
const API_BASE = window.location.origin;

// State
let currentUser = null;
let authToken = null;

// Initialize app
document.addEventListener('DOMContentLoaded', () => {
    initAuth();
    initEventListeners();
});

// Auth initialization
function initAuth() {
    authToken = localStorage.getItem('authToken');
    
    if (authToken) {
        loadCurrentUser();
    } else {
        showLoginScreen();
    }
}

// Event Listeners
function initEventListeners() {
    // Tab switching
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => switchAuthTab(btn.dataset.tab));
    });

    // Auth forms
    document.getElementById('login-form').addEventListener('submit', handleLogin);
    document.getElementById('register-form').addEventListener('submit', handleRegister);
    
    // Dashboard
    document.getElementById('logout-btn').addEventListener('click', handleLogout);
    
    // Dashboard tabs
    document.querySelectorAll('.dashboard-tab-btn').forEach(btn => {
        btn.addEventListener('click', () => switchDashboardTab(btn.dataset.tab));
    });

    // API Key management
    document.getElementById('create-key-btn').addEventListener('click', showCreateKeyForm);
    document.getElementById('cancel-key-btn').addEventListener('click', hideCreateKeyForm);
    document.getElementById('new-key-form').addEventListener('submit', handleCreateApiKey);
}

// Switch auth tabs
function switchAuthTab(tab) {
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    
    document.querySelectorAll('.auth-form').forEach(form => {
        form.classList.toggle('active', form.id === `${tab}-form`);
    });

    // Clear error messages
    document.getElementById('login-error').classList.remove('show');
    document.getElementById('register-error').classList.remove('show');
}

// Switch dashboard tabs
function switchDashboardTab(tab) {
    document.querySelectorAll('.dashboard-tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    
    document.querySelectorAll('.dashboard-content').forEach(content => {
        content.classList.toggle('active', content.id === `${tab}-tab`);
    });

    if (tab === 'users') {
        loadUsers();
    }
}

// Show/Hide screens
function showLoginScreen() {
    document.getElementById('login-screen').style.display = 'block';
    document.getElementById('dashboard-screen').style.display = 'none';
}

function showDashboard() {
    document.getElementById('login-screen').style.display = 'none';
    document.getElementById('dashboard-screen').style.display = 'block';
    
    // Update user info
    document.getElementById('user-info').textContent = 
        `${currentUser.name} (Level ${currentUser.level})`;
    
    // Show users tab if level > 2
    if (currentUser.level > 2) {
        document.getElementById('users-tab-btn').style.display = 'block';
    }
    
    // Show/hide create API key button based on level
    const createKeyBtn = document.getElementById('create-key-btn');
    if (currentUser.level >= 2) {
        createKeyBtn.style.display = 'block';
    } else {
        createKeyBtn.style.display = 'none';
    }
    
    // Load API keys
    loadApiKeys();
}

// API calls
async function apiCall(endpoint, options = {}) {
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Ein Fehler ist aufgetreten');
    }

    return data;
}

// Load current user
async function loadCurrentUser() {
    try {
        const data = await apiCall('/auth/me');
        currentUser = data.user;
        showDashboard();
    } catch (error) {
        console.error('Failed to load user:', error);
        localStorage.removeItem('authToken');
        authToken = null;
        showLoginScreen();
    }
}

// Handle login
async function handleLogin(e) {
    e.preventDefault();
    
    const email = document.getElementById('login-email').value;
    const password = document.getElementById('login-password').value;
    const errorEl = document.getElementById('login-error');

    try {
        const data = await apiCall('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });

        authToken = data.token;
        localStorage.setItem('authToken', authToken);
        currentUser = data.user;
        
        showDashboard();
    } catch (error) {
        errorEl.textContent = error.message;
        errorEl.classList.add('show');
    }
}

// Handle register
async function handleRegister(e) {
    e.preventDefault();
    
    const name = document.getElementById('register-name').value;
    const email = document.getElementById('register-email').value;
    const password = document.getElementById('register-password').value;
    const errorEl = document.getElementById('register-error');

    try {
        await apiCall('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ name, email, password }),
        });

        // Auto-login after registration
        const loginData = await apiCall('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });

        authToken = loginData.token;
        localStorage.setItem('authToken', authToken);
        currentUser = loginData.user;
        
        showDashboard();
    } catch (error) {
        errorEl.textContent = error.message;
        errorEl.classList.add('show');
    }
}

// Handle logout
function handleLogout() {
    authToken = null;
    currentUser = null;
    localStorage.removeItem('authToken');
    showLoginScreen();
}

// Load API keys
async function loadApiKeys() {
    try {
        const data = await apiCall('/api-keys');
        displayApiKeys(data.apiKeys);
    } catch (error) {
        console.error('Failed to load API keys:', error);
    }
}

// Display API keys
function displayApiKeys(keys) {
    const listEl = document.getElementById('api-keys-list');
    
    // Show info message for users below level 2
    let levelWarning = '';
    if (currentUser.level < 2) {
        levelWarning = `
            <div class="card" style="background: #fff3cd; border-left: 4px solid #ffc107;">
                <p style="margin: 0; color: #856404;">
                    <strong>ℹ️ Hinweis:</strong> Sie benötigen mindestens Level 2, um API Keys zu erstellen. 
                    Kontaktieren Sie einen Administrator, um Ihr Level zu erhöhen.
                </p>
            </div>
        `;
    }
    
    if (keys.length === 0) {
        listEl.innerHTML = levelWarning + `
            <div class="empty-state">
                <h3>Keine API Keys vorhanden</h3>
                <p>${currentUser.level >= 2 ? 'Erstellen Sie Ihren ersten API Key, um loszulegen.' : 'Sie haben noch keine API Keys.'}</p>
            </div>
        `;
        return;
    }

    listEl.innerHTML = levelWarning + keys.map(key => {
        const isExpired = key.expiresAt && new Date(key.expiresAt) < new Date();
        const statusClass = isExpired ? 'expired' : (key.isActive ? 'active' : 'inactive');
        const statusText = isExpired ? 'Abgelaufen' : (key.isActive ? 'Aktiv' : 'Inaktiv');
        
        return `
            <div class="key-item">
                <div class="key-info">
                    <h4>${escapeHtml(key.name)}</h4>
                    <p>Erstellt: ${formatDate(key.createdAt)}</p>
                    ${key.lastUsed ? `<p>Zuletzt verwendet: ${formatDate(key.lastUsed)}</p>` : ''}
                    ${key.expiresAt ? `<p>Läuft ab: ${formatDate(key.expiresAt)}</p>` : '<p>Kein Ablaufdatum</p>'}
                    <span class="key-status ${statusClass}">${statusText}</span>
                </div>
                <div>
                    <button class="btn btn-danger" onclick="deleteApiKey('${key.id}')">Löschen</button>
                </div>
            </div>
        `;
    }).join('');
}

// Show/Hide create key form
function showCreateKeyForm() {
    document.getElementById('create-key-form').style.display = 'block';
    document.getElementById('new-key-error').classList.remove('show');
    document.getElementById('new-key-success').classList.remove('show');
}

function hideCreateKeyForm() {
    document.getElementById('create-key-form').style.display = 'none';
    document.getElementById('new-key-form').reset();
    document.getElementById('new-key-error').classList.remove('show');
    document.getElementById('new-key-success').classList.remove('show');
}

// Handle create API key
async function handleCreateApiKey(e) {
    e.preventDefault();
    
    const name = document.getElementById('key-name').value;
    const expiresInDays = document.getElementById('key-expires').value;
    const errorEl = document.getElementById('new-key-error');
    const successEl = document.getElementById('new-key-success');

    try {
        const body = { name };
        if (expiresInDays) {
            body.expiresInDays = parseInt(expiresInDays);
        }

        const data = await apiCall('/api-keys', {
            method: 'POST',
            body: JSON.stringify(body),
        });

        successEl.innerHTML = `
            <strong>API Key erfolgreich erstellt!</strong><br>
            <div class="key-value">${data.apiKey.key}</div>
            <p style="margin-top: 0.5rem; color: #856404;">⚠️ Speichern Sie diesen Key sicher. Er wird nur einmal angezeigt!</p>
        `;
        successEl.classList.add('show');
        
        document.getElementById('new-key-form').reset();
        
        // Reload keys after 3 seconds
        setTimeout(() => {
            hideCreateKeyForm();
            loadApiKeys();
        }, 5000);
    } catch (error) {
        errorEl.textContent = error.message;
        errorEl.classList.add('show');
    }
}

// Delete API key
async function deleteApiKey(id) {
    if (!confirm('Möchten Sie diesen API Key wirklich löschen?')) {
        return;
    }

    try {
        await apiCall(`/api-keys/${id}`, {
            method: 'DELETE',
        });

        loadApiKeys();
    } catch (error) {
        alert('Fehler beim Löschen: ' + error.message);
    }
}

// Load users
async function loadUsers() {
    try {
        const data = await apiCall('/users');
        displayUsers(data.users);
    } catch (error) {
        console.error('Failed to load users:', error);
        document.getElementById('users-list').innerHTML = `
            <div class="empty-state">
                <h3>Fehler beim Laden der Benutzer</h3>
                <p>${error.message}</p>
            </div>
        `;
    }
}

// Display users
function displayUsers(users) {
    const listEl = document.getElementById('users-list');
    
    if (users.length === 0) {
        listEl.innerHTML = `
            <div class="empty-state">
                <h3>Keine Benutzer vorhanden</h3>
            </div>
        `;
        return;
    }

    listEl.innerHTML = users.map(user => {
        const isCurrentUser = user.id === currentUser.id;
        
        return `
            <div class="user-item">
                <div class="user-header">
                    <div class="user-info">
                        <h4>${escapeHtml(user.name)} ${isCurrentUser ? '(Sie)' : ''}</h4>
                        <p>${escapeHtml(user.email)}</p>
                        <p>Registriert: ${formatDate(user.createdAt)}</p>
                    </div>
                    <span class="user-badge">Level ${user.level}</span>
                </div>
                ${!isCurrentUser ? `
                    <div class="user-actions">
                        <div class="level-form">
                            <label>Level ändern:</label>
                            <input type="number" id="level-${user.id}" min="0" max="10" value="${user.level}">
                            <button class="btn btn-success" onclick="updateUserLevel('${user.id}')">Speichern</button>
                        </div>
                        <button class="btn btn-danger" onclick="deleteUser('${user.id}')">Benutzer löschen</button>
                    </div>
                ` : ''}
            </div>
        `;
    }).join('');
}

// Update user level
async function updateUserLevel(userId) {
    const levelInput = document.getElementById(`level-${userId}`);
    const level = parseInt(levelInput.value);

    try {
        await apiCall(`/users/${userId}/level`, {
            method: 'PUT',
            body: JSON.stringify({ level }),
        });

        loadUsers();
    } catch (error) {
        alert('Fehler beim Aktualisieren: ' + error.message);
    }
}

// Delete user
async function deleteUser(userId) {
    if (!confirm('Möchten Sie diesen Benutzer wirklich löschen? Alle zugehörigen API Keys werden ebenfalls gelöscht.')) {
        return;
    }

    try {
        await apiCall(`/users/${userId}`, {
            method: 'DELETE',
        });

        loadUsers();
    } catch (error) {
        alert('Fehler beim Löschen: ' + error.message);
    }
}

// Utility functions
function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
